/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, CircularProgress, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import QuizColumns from './QuizColumns';
import QuizListHeader from './QuizListHeader';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import TableDense from '../../components/common/TableDense';
import { useAppContext } from '../../contexts/AppContext';
import QuizCollectionService from '../../services/quizCollection.service';

const quizCollectionService = new QuizCollectionService();

const useStyle = makeStyles((theme) => {
  return {
    headerContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      marginBottom: 32,
    },
    headerContainerLeft: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      maxWidth: '500px',
      width: '100%',
    },
    headerContainerRight: {
      alignItems: 'end',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      maxWidth: '500px',
      width: '100%',
    },
    linkBack: {
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'flex',
      flexWrap: 'nowrap',
      fontSize: '14px',
      fontWeight: '600',
      justifyContent: 'start',
      textDecoration: 'none',
    },

    linkBackIcon: {
      marginRight: '0px',
    },
    tableActionColumnContainer: {
      alignItems: 'center',
      color: theme.palette.grey['500'],
      cursor: 'pointer',
      display: 'flex',
      flexWrap: 'nowrap',
      fontSize: '12px',
      fontWeight: 600,
      justifyContent: 'start',
      textDecoration: 'none',
    },
    tableActionIcon: {
      color: theme.palette.grey['500'],
      fontSize: '20px',
      marginRight: '4px',
    },
    tableDescription: {
      color: theme.palette.grey['500'],
    },
    tablePrivacyGlobal: {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '4px',
      color: '#fff',
      fontSize: '10px',
      fontWeight: '600',
      padding: '2px 6px',
      textTransform: 'uppercase',
    },
    tablePrivacyPrivate: {
      backgroundColor: theme.palette.grey['500'],
      borderRadius: '4px',
      color: '#fff',
      fontSize: '10px',
      fontWeight: '600',
      padding: '2px 6px',
      textTransform: 'uppercase',
    },
    tablePrivacyUser: {
      alignItems: 'center',
      color: theme.palette.grey['500'],
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '4px',
    },
    tablePrivacyWorkspace: {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '4px',
      color: '#fff',
      fontSize: '10px',
      fontWeight: '600',
      padding: '2px 6px',
      textTransform: 'uppercase',
    },
    tableTagsChip: {
      marginBottom: '4px',
      marginRight: '4px',
      marginTop: '4px',
    },
    tableTagsColumnContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      justifyContent: 'start',
    },
    tableTitle: {
      color: theme.palette.grey['900'],
      fontWeight: '600',
    },
  };
});

const QuizzesView = () => {
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAppContext();

  const [quizCollections, setQuizCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPrivacy, setSelectedPrivacy] = useState('');
  const [search, setSearch] = useState('');
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchQuizCollections = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const response = await quizCollectionService.getQuizCollections({
          limit: 15,
          page: 1,
          ...params,
        });

        setQuizCollections(response.data || []);
        setTotalData(response.pagination?.total || 0);
        setCurrentPage(params.page || 1);
      } catch (error) {
        console.error('Error fetching quizzes:', error);

        enqueueSnackbar('Failed to load quizzes', { variant: 'error' });
        setQuizCollections([]);
        setTotalData(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const fetchTags = useCallback(async () => {
    try {
      setIsTagsLoading(true);

      const response = await quizCollectionService.getAllTags();

      setTags(response.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);

      enqueueSnackbar('Failed to load tags', { variant: 'error' });
      setTags([]);
    } finally {
      setIsTagsLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleChangePrivacy = useCallback(
    (privacy) => {
      setSelectedPrivacy(privacy);
      fetchQuizCollections({ page: 1, privacy, search, tags: selectedTags });
    },
    [selectedTags, search, fetchQuizCollections]
  );

  const handleChangeTag = useCallback(
    (newSelectedTags) => {
      setSelectedTags(newSelectedTags);
      fetchQuizCollections({ page: 1, privacy: selectedPrivacy, search, tags: newSelectedTags });
    },
    [selectedPrivacy, search, fetchQuizCollections]
  );

  const handleChangeSearch = useCallback((newSearch) => {
    setSearch(newSearch);
  }, []);

  const handleClearClick = useCallback(() => {
    setSelectedTags([]);
    setSelectedPrivacy('');
    setSearch('');
    fetchQuizCollections({ page: 1 });
  }, [fetchQuizCollections]);

  const handleSearch = useCallback(() => {
    fetchQuizCollections({ page: 1, privacy: selectedPrivacy, search, tags: selectedTags });
  }, [selectedPrivacy, search, selectedTags, fetchQuizCollections]);

  const handleChangePage = useCallback(
    (page) => {
      fetchQuizCollections({ page, privacy: selectedPrivacy, search, tags: selectedTags });
    },
    [selectedPrivacy, search, selectedTags, fetchQuizCollections]
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleDeleteQuiz = useCallback((quizId, quizTitle) => {
    setQuizToDelete({ id: quizId, title: quizTitle });
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!quizToDelete) return;

    try {
      setIsDeleting(true);
      await quizCollectionService.deleteQuizCollection(quizToDelete.id);

      enqueueSnackbar('Quiz deleted successfully', { variant: 'success' });

      fetchQuizCollections({ page: currentPage, privacy: selectedPrivacy, search, tags: selectedTags });
      setDeleteModalOpen(false);
      setQuizToDelete(null);
    } catch (error) {
      console.error('Error deleting quiz:', error);
      enqueueSnackbar('Failed to delete quiz', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [quizToDelete, currentPage, selectedPrivacy, search, selectedTags, fetchQuizCollections, enqueueSnackbar]);

  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setQuizToDelete(null);
    setIsDeleting(false);
  }, []);

  const userName = useMemo(() => {
    return currentUser?.name || '';
  }, [currentUser]);

  useEffect(() => {
    fetchQuizCollections({ page: 1 });
    fetchTags();
  }, [fetchQuizCollections, fetchTags]);

  const tableOptions = useMemo(() => {
    return {
      count: totalData,
      onChangePage: handleChangePage,
      page: currentPage - 1,
    };
  }, [currentPage, totalData, handleChangePage]);

  const isMediphanyWorkspace = useMemo(() => {
    return currentUser?.workspace?.isMediphanyWorkspace || false;
  }, [currentUser]);

  const workspaceName = useMemo(() => {
    return currentUser?.workspace?.name || '';
  }, [currentUser]);

  return (
    <Grid container component={Container} style={{ display: 'flex', marginTop: '30px', textAlign: 'left', width: '100vw' }}>
      <Grid item xs={12}>
        <div className={classes.headerContainer}>
          <Typography className={`page-header mb-0 ${classes.header}`}>Quizzes</Typography>

          <Link to="/quizzes/new">
            <Button color="primary" variant="contained">
              Create New Quiz
            </Button>
          </Link>
        </div>
      </Grid>

      <Grid item xs={12}>
        <QuizListHeader
          selectedPrivacy={selectedPrivacy}
          selectedTags={selectedTags}
          handleChangePrivacy={handleChangePrivacy}
          handleChangeSearch={handleChangeSearch}
          handleChangeTag={handleChangeTag}
          handleSearch={handleSearch}
          isMediphanyWorkspace={isMediphanyWorkspace}
          handleSearchKeyDown={handleSearchKeyDown}
          loading={loading}
          search={search}
          tags={tags}
          isTagsLoading={isTagsLoading}
          onClear={handleClearClick}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={{ marginTop: '24px' }}>
          {loading ? <LinearProgress /> : null}
          <TableDense
            tableId="quiz-list"
            options={tableOptions}
            data={quizCollections || []}
            columns={QuizColumns({ classes, currentUserName: userName, isMediphanyWorkspace, onDelete: handleDeleteQuiz, workspaceName })}
          />
        </div>
      </Grid>

      <ConfirmationModal
        open={deleteModalOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Quiz"
        message={`Are you sure you want to delete "${quizToDelete?.title}"? This action cannot be undone.`}
        confirmText={
          isDeleting ? (
            <>
              <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
              Deleting...
            </>
          ) : (
            'Delete Quiz'
          )
        }
        cancelText="Cancel"
      />
    </Grid>
  );
};

export default QuizzesView;
